/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import { HiPlus } from 'react-icons/hi';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

import { IoLogInOutline } from 'react-icons/io5';
import api from '~/services/api';
import Toast from '~/utils/toast';
import { formatPrice } from '~/utils/format';

import { Cards, Container, Filter } from './styles';
import FiltersLists from '~/components/FiltersLists';
import { KrClose, KrEdit } from '~/components/KoroIcons';
import InfiniteScroll from '~/components/InfiniteScroll';
import Loading from '~/components/Loading';
import { IOption } from '~/components/Select';

interface IItem {
  id: number;
  price: string;
}

interface IOrder {
  id: number;
  service_fee: string;
  processing_fee: string;
  discount: string;
  total: string;
  items: IItem[];
}

interface IBatch {
  id: number;
  quantity: number;
}

interface IEvent {
  id: number;
  batches: IBatch[];
  orders: IOrder[];
}

interface IOrganizerData {
  id: number;
  name: string;
  report: string;
  events_quantity: number;
  created_at: string;
}

interface IOrganizerResponse {
  data: IOrganizerData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IOrganizers {
  id: number;
  organizer: string;
  date: string;
  totalEvents: number;
  report: boolean;
}

const Organizers: React.FC = () => {
  const [organizers, setOrganizers] = useState<IOrganizers[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [organizerSelected, setOrganizerSelected] = useState('');

  const [organizersFilter, setOrganizersFilter] = useState<IOption[]>([]);

  interface IOrganizer {
    id: number;
    name: string;
  }

  useEffect(() => {
    api.get<IOrganizer[]>('organizers').then((response) => {
      const dataOrganizers = response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));
      setOrganizersFilter(dataOrganizers);
    });
  }, []);

  const handleLoadOrganizers = useCallback(
    async (
      pageData,
      organizerData?: string,
      dateStart?: string,
      dateEnd?: string
    ) => {
      try {
        setLoadingMore(true);
        const response = await api
          .get<IOrganizerResponse>('organizers', {
            params: {
              page: pageData,
              organizer: organizerData,
              startDate: dateStart ? new Date(dateStart) : undefined,
              endDate: dateEnd ? new Date(dateEnd) : undefined,
            },
          })
          .finally(() => {
            setLoadingMore(false);
          });

        const data = response.data.data.map<IOrganizers>((organizer) => {
          return {
            id: organizer.id,
            organizer: organizer.name,
            date: format(parseISO(organizer.created_at), 'dd/MM/yyyy'),
            totalEvents: organizer.events_quantity,
            report: organizer.report === '1',
          };
        });

        if (pageData === 1) {
          setOrganizers(data);
        } else {
          setOrganizers((oldState) => [...oldState, ...data]);
        }
        setTotalPages(response.data.pages);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    handleLoadOrganizers(1);
  }, [handleLoadOrganizers]);

  const handleClickDelete = useCallback((organizer) => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ff6900',
      title: `Gostaria de excluir o organizador?`,
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#B34A00',
      reverseButtons: true,
    }).then(async (e) => {
      if (e.isConfirmed) {
        await api.delete(`organizers/${organizer.id}`);
        setOrganizers((oldState) =>
          oldState.filter((organizerData) => organizerData.id !== organizer.id)
        );
        Toast.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: 'Organizador excluído com sucesso!',
        });
      }
    });
  }, []);

  const handleClickLogin = useCallback((organizer) => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ff6900',
      title: `Gostaria de logar como o organizador?`,
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#B34A00',
      reverseButtons: true,
    }).then(async (e) => {
      if (e.isConfirmed) {
        const responseOrganizer = await api.get(`organizers/${organizer.id}`);
        const response = await api.post(
          `users/sessions/set-login/${responseOrganizer.data.organizersUsers[0].user_id}`,
          {
            type: 'Organizador',
          }
        );
        window.open(
          `${process.env.REACT_APP_ORGANIZER_URL}/auto-login/${response.data.token}`,
          '_blank'
        );
      }
    });
  }, []);

  const handleLoad = useCallback(async () => {
    if (!loadingMore) {
      setLoadingMore(true);
      if (page < totalPages) {
        await handleLoadOrganizers(page + 1, organizerSelected);
        setPage(page + 1);
      } else {
        setLoadingMore(false);
      }
    }
  }, [loadingMore, page, totalPages, handleLoadOrganizers, organizerSelected]);

  const handleSubmitFilters = useCallback(
    (data) => {
      setOrganizerSelected(
        data.organizer === 'todos' ? undefined : data.organizer
      );
      handleLoadOrganizers(
        1,
        data.organizer === 'todos' ? undefined : data.organizer
      );
      setPage(1);
    },
    [handleLoadOrganizers]
  );

  return (
    <Container className="mt-n3 mb-n3">
      <InfiniteScroll
        id="infinite-scroll"
        onInfiniteLoad={handleLoad}
        className="infinite-scroll py-3"
      >
        <div className="container-fluid py-3">
          <div className="row">
            <div className="zoom col-12 px-0">
              <div className="bg-white d-flex justify-content-between align-items-center p-4">
                <h1 className="mb-0">Organizadores</h1>
                <div>
                  <Link
                    to={`${process.env.PUBLIC_URL}/organizadores/novo-organizador`}
                    className="mb-0 h6 fw-normal text-white btn-new px-4 py-2"
                  >
                    <HiPlus size={16} color="#fff" className="me-2" />
                    Novo Organizador
                  </Link>
                </div>
              </div>
            </div>
            <Filter className="col-12 px-0 mt-3">
              <FiltersLists
                onSubmitFilters={handleSubmitFilters}
                organizer
                organizersData={organizersFilter}
              />
            </Filter>

            <div className="zoom col-12 pe-0">
              {organizers.map((organizer) => (
                <Cards className="bg-white p-3 mt-3">
                  <div className="data-row d-flex  justify-content-between align-items-center">
                    <div>
                      <h3 className="mb-1">Nome Organizador</h3>
                      <p className="mb-0">
                        {organizer.id} - {organizer.organizer}
                      </p>
                    </div>
                    <div>
                      <h3 className="mb-1">Data início</h3>
                      <p className="mb-0">{organizer.date}</p>
                    </div>
                    <div>
                      <h3 className="mb-1">Total eventos</h3>
                      <p className="mb-0">{organizer.totalEvents}</p>
                    </div>
                    <div>
                      <h3 className="mb-1">Reporte</h3>
                      <p className="mb-0">
                        {organizer.report ? 'Ativo' : 'Inativo'}
                      </p>
                    </div>
                    <div>
                      <button
                        type="button"
                        className="border-0 rounded-circle bg-orange"
                        onClick={() => handleClickLogin(organizer)}
                      >
                        <IoLogInOutline size={16} color="#fff" />
                      </button>
                      <Link
                        to={`${process.env.PUBLIC_URL}/organizadores/editar-organizador/${organizer.id}`}
                        className="border-0 rounded-circle bg-orange mx-1"
                      >
                        <KrEdit size={16} color="#fff" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleClickDelete(organizer)}
                        className="border-0 rounded-circle bg-orange"
                      >
                        <KrClose size={16} color="#fff" />
                      </button>
                    </div>
                  </div>
                </Cards>
              ))}
            </div>
          </div>
        </div>
      </InfiniteScroll>
      <Loading active={loadingMore} text="Carregando..." />
    </Container>
  );
};

export default Organizers;
